// services/api.js
//
// Shared API client. Every failed request rejects with an ApiError that carries
// the backend's Czech message (`error`, legacy `message`), the HTTP status and
// the row-level `details` (e.g. Excel import). A 401 fires the window event
// `auth:expired`, which AuthContext turns into a logout (audit FL-02, FL-03).
import axios from 'axios';
import config from '../config';

export const MESSAGES = {
  network: 'Nepodařilo se spojit se serverem. Zkontrolujte připojení k internetu a zkuste to znovu.',
  server: 'Na serveru došlo k chybě. Zkuste to prosím za chvíli znovu. Pokud potíže přetrvají, napište na info@bestofthebest.cz.',
  expired: 'Vaše přihlášení vypršelo. Přihlaste se prosím znovu.',
  forbidden: 'K této akci nemáte oprávnění.',
  notFound: 'Požadovaný záznam nebyl nalezen.',
  tooLarge: 'Soubor je příliš velký.',
  unknown: 'Něco se nepovedlo. Zkuste to prosím znovu.',
};

export class ApiError extends Error {
  constructor(message, { status = null, details = null, data = null, cause = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.data = data;
    this.cause = cause;
  }
}

function fallbackMessage(status) {
  if (status === 401) return MESSAGES.expired;
  if (status === 403) return MESSAGES.forbidden;
  if (status === 404) return MESSAGES.notFound;
  if (status === 413) return MESSAGES.tooLarge;
  if (status >= 500) return MESSAGES.server;
  return MESSAGES.unknown;
}

/** Convert any axios / network error into an ApiError (idempotent). */
export function toApiError(error) {
  if (error instanceof ApiError) return error;
  if (error && error.response) {
    const { status, data } = error.response;
    const body = data && typeof data === 'object' ? data : {};
    // nginx answers 413 with an HTML page, so the status decides the text there.
    const message = status === 413
      ? MESSAGES.tooLarge
      : (typeof body.error === 'string' && body.error) || (typeof body.message === 'string' && body.message) || fallbackMessage(status);
    return new ApiError(message, { status, details: body.details || null, data, cause: error });
  }
  if (error && error.request) {
    return new ApiError(MESSAGES.network, { cause: error });
  }
  return new ApiError((error && error.message) || MESSAGES.unknown, { cause: error });
}

/** Human-readable Czech message for any error thrown by a request. */
export function getErrorMessage(error, fallback) {
  const apiError = toApiError(error);
  if (fallback && apiError.message === MESSAGES.unknown) return fallback;
  return apiError.message;
}

function notifyExpired(error) {
  if (error && error.response && error.response.status === 401 && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:expired'));
  }
}

// No default Content-Type: axios sets JSON for objects and leaves multipart to the browser for
// FormData. A JSON default makes it serialise FormData to {"file":{}}, so no file arrives.
const api = axios.create({
  baseURL: config.API_BASE_URL,
});

api.interceptors.request.use((requestConfig) => {
  const token = localStorage.getItem('token');
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    notifyExpired(error);
    return Promise.reject(toApiError(error));
  }
);

// Pages that still call the global axios directly keep their error objects,
// but an expired session is detected for them as well.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    notifyExpired(error);
    return Promise.reject(error);
  }
);

export default api;

// Dancers API
export const dancersApi = {
  getAll: () => api.get('/dancers/'),
  getArchived: () => api.get('/dancers/', { params: { archived: 1 } }),
  create: (data) => api.post('/dancers/', data),
  delete: (id) => api.delete(`/dancers/${id}`),
  archive: (ids) => api.post('/dancers/archive', { ids }),
  restore: (id) => api.post(`/dancers/${id}/restore`),
  duplicates: () => api.get('/dancers/duplicates'),
  merge: (keepId, mergeIds) => api.post('/dancers/merge', { keepId, mergeIds }),
  // New Excel-related endpoints
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/dancers/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  bulkCreate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/dancers/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadSample: () => {
    window.location.href = `${config.API_BASE_URL}/dancers/sample-xlsx`;
  }
};

// Rest of the file remains unchanged...
export const competitionsApi = {
  getAll: () => api.get('/competitions/'),
  getById: (id) => api.get(`/competitions/${id}`)
};

export const choreographiesApi = {
  getAll: () => api.get('/choreographies/'),
  getQualified: () => api.get('/choreographies/qualified'),
  getById: (id) => api.get(`/choreographies/${id}`),
  create: (data) => api.post('/choreographies/', data),
  register: (id) => api.post(`/choreographies/${id}/register`),
  registerForFinale: (id, finaleId) => api.post(`/choreographies/${id}/register-for-finale`, { finaleId }),
  updateDancers: (id, dancers) => api.put(`/choreographies/${id}/dancers`, { dancers }),
  uploadMusic: (id, formData) => api.post(`/choreographies/${id}/music`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadBackgroundVideo: (id, formData) => api.post(`/choreographies/${id}/background-video`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  confirmPresence: (id) => api.post(`/choreographies/${id}/presence`),
  delete: (id) => api.delete(`/choreographies/${id}`),
  update: (id, data) => api.put(`/choreographies/${id}`, data),
};

export const calculateAge = (birthDate, competitionDate) => {
  const birthYear = new Date(birthDate).getFullYear();
  const competitionYear = new Date(competitionDate).getFullYear();
  return competitionYear - birthYear;
};

// Age category ranges matching backend (competitions.py)
// Tolerance: 2 years younger, 1 year older (asymmetric) - max 50% of formation
// Same table as backend/app/utils/age_rules.py (requirement 2.5): Baby never under 3,
// Senioři 25+ without tolerance. Pages read the live table from GET /api/meta.
const AGE_CATEGORY_RULES = {
  'BABY': { min: 3, max: 8, toleranceYounger: 0, toleranceOlder: 1 },
  'CHILDREN': { min: 6, max: 12, toleranceYounger: 2, toleranceOlder: 1 },
  'JUNIOR': { min: 11, max: 16, toleranceYounger: 2, toleranceOlder: 1 },
  'ADULT': { min: 16, max: null, toleranceYounger: 2, toleranceOlder: 0 },  // 16+ years
  'SENIOR': { min: 25, max: null, toleranceYounger: 0, toleranceOlder: 0 },  // 25+ years, no tolerance
};

export const validateAgeCategory = (age, category) => {
  const categoryRules = AGE_CATEGORY_RULES[category];

  if (!categoryRules) return { valid: true, type: 'direct' };

  const { min: minAge, max: maxAge, toleranceYounger, toleranceOlder } = categoryRules;

  // Check if age is within normal range
  if (maxAge !== null) {
    // Category with upper limit (BABY, CHILDREN, JUNIOR)
    if (age >= minAge && age <= maxAge) {
      return { valid: true, type: 'direct' };
    }
    // Check flexible range: 2 years younger OR 1 year older
    if (age >= (minAge - toleranceYounger) && age <= (maxAge + toleranceOlder)) {
      return { valid: true, type: 'tolerance' };
    }
  } else {
    // Category with no upper limit (ADULT: 16+)
    if (age >= minAge) {
      return { valid: true, type: 'direct' };
    }
    // Flexible: 2 years younger allowed
    if (age >= (minAge - toleranceYounger)) {
      return { valid: true, type: 'tolerance' };
    }
  }

  return { valid: false };
};

export const validateChoreographyDancers = (dancers, category, competitionDate) => {
  if (!dancers || dancers.length === 0) {
    return { valid: false, error: 'Není vybrán žádný tanečník' };
  }

  // For BABY category, ensure no dancer is younger than 3 years
  if (category === 'BABY') {
    const tooYoungDancers = dancers.filter(dancer => {
      const age = calculateAge(dancer.birthDate, competitionDate);
      return age < 3;
    });

    if (tooYoungDancers.length > 0) {
      return {
        valid: false,
        error: `${tooYoungDancers.length} tanečník(ů) je mladší než 3 roky`
      };
    }
  }

  let toleranceDancers = 0;
  let invalidDancers = 0;

  dancers.forEach(dancer => {
    const age = calculateAge(dancer.birthDate, competitionDate);
    const validation = validateAgeCategory(age, category);
    
    if (!validation.valid) {
      invalidDancers++;
    } else if (validation.type === 'tolerance') {
      toleranceDancers++;
    }
  });

  // Check if more than 50% of dancers are using age tolerance
  const tolerancePercentage = (toleranceDancers / dancers.length) * 100;

  if (invalidDancers > 0) {
    return {
      valid: false,
      error: `${invalidDancers} tanečník(ů) nesplňuje věkové požadavky kategorie`
    };
  }

  if (tolerancePercentage > 50) {
    return {
      valid: false,
      error: 'Více než 50% tanečníků využívá věkovou toleranci'
    };
  }

  return { valid: true };
};

export const getAgeDistribution = (dancers, competitionDate) => {
  const ages = dancers.map(dancer => calculateAge(dancer.birthDate, competitionDate));
  
  const categoryStats = dancers.reduce((acc, dancer) => {
    const age = calculateAge(dancer.birthDate, competitionDate);
    const validation = validateAgeCategory(age, dancer.ageCategory);
    
    if (validation.valid) {
      if (validation.type === 'direct') {
        acc.coreDancers++;
      } else if (validation.type === 'tolerance') {
        acc.toleranceDancers++;
      }
    }
    return acc;
  }, { coreDancers: 0, toleranceDancers: 0 });

  return {
    min: Math.min(...ages),
    max: Math.max(...ages),
    avg: Math.round(ages.reduce((a, b) => a + b, 0) / ages.length),
    distribution: ages.reduce((acc, age) => {
      acc[age] = (acc[age] || 0) + 1;
      return acc;
    }, {}),
    ...categoryStats,
    tolerancePercentage: Math.round((categoryStats.toleranceDancers / dancers.length) * 100)
  };
};